/**
 Project	ENT - Environnement numérique de travail

 @file 	    navigation.service.ts
 @author  	Cortes Zuka Luan <luan.cortes@ictvs.ch>
 @copyright © 2023, ICT-vs. All rights reserved.
**/

import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { EntNavigation } from 'src/app/navigation/ent-navigation';
import { environment } from 'src/environments/environment';
import { adminPanelItem, adminPanel } from 'src/app/navigation/navigation';
import { RouteStatusService } from 'src/app/services/maintenance/route-status.service';

@Injectable({
  providedIn: 'root',
})
export class EntNavigationService {
  // Private
  private _onNavigationChanged: BehaviorSubject<null | string>;
  private _currentNavigationKey: string;
  private _registry: Record<string, EntNavigation[]> = {};

  administrationStatus = false;
  private subscription: Subscription = new Subscription();

  /**
   * Constructor
   */
  constructor(private _routeStatusService: RouteStatusService) {
    // Set the private defaults
    this._currentNavigationKey = null;
    this._onNavigationChanged = new BehaviorSubject(null);
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  /**
   * Get onNavigationChanged observable
   */
  get onNavigationChanged(): Observable<string> {
    return this._onNavigationChanged.asObservable();
  }

  /**
   * Register the given navigation
   * with the given key
   *
   * @param key
   * @param navigation
   */
  register(key: string, navigation: EntNavigation[]): void {
    // Check if the key already being used
    if (this._registry[key]) {
      console.error(
        `The navigation with the key '${key}' already exists. Either unregister it first or use a unique key.`
      );

      return;
    }

    // Add to the registry
    this._registry[key] = navigation;
  }

  /**
   * Unregister the navigation from the registry
   * @param key
   */
  unregister(key: string): void {
    // Check if the navigation exists
    if (!this._registry[key]) {
      console.warn(
        `The navigation with the key '${key}' doesn't exist in the registry.`
      );
    }

    // Unregister the sidebar
    delete this._registry[key];
  }

  /**
   * Get navigation from registry by key
   *
   * @param key
   * @returns {any}
   */
  getNavigation(key: string): EntNavigation[] {
    // Check if the navigation exists
    if (!this._registry[key]) {
      console.warn(
        `The navigation with the key '${key}' doesn't exist in the registry.`
      );

      return;
    }

    // Return the sidebar
    return this._registry[key];
  }

  /**
   * Get the current navigation
   *
   * @returns {any}
   */
  getCurrentNavigation(): EntNavigation[] {
    if (!this._currentNavigationKey) {
      console.warn(`The current navigation is not set.`);

      return;
    }

    return this.getNavigation(this._currentNavigationKey);
  }

  /**
   * Set the navigation with the key
   * as the current navigation
   *
   * @param key
   */
  setCurrentNavigation(key: string, hasAccessAdministration = false) {
    // Check if the sidebar exists
    if (!this._registry[key]) {
      console.warn(
        `The navigation with the key '${key}' doesn't exist in the registry.`
      );
      return;
    }

    // Set the current navigation key
    this._currentNavigationKey = key;

    // Check if accss to administration panel to add it to current nav
    if (hasAccessAdministration) {
      // Find the 'administrations' navigation panel
      let adminNav = this._registry[key].find(
        nav => nav.id === 'administration'
      );

      // If administrations panel doesn't exist, create it
      if (!adminNav) {
        this._registry[key].push(adminPanel);
        // Refresh the reference to the newly added administration panel
        adminNav = this._registry[key].find(nav => nav.id === 'administration');
      }

      const childrenExists = adminNav.children.some(
        item => item.id === 'administration-panel'
      );
      if (adminNav && !childrenExists) {
        // While the item is not part of static nav object, we need to get the maintenance status to set it
        const urls = ['/administration'];
        this.subscription.add(
          this._routeStatusService
            .getRouteStatusForUrls(urls)
            .subscribe(statuses => {
              this.administrationStatus = statuses['/administration'];
              adminPanelItem.maintenance = this.administrationStatus;
            })
        );
        // Add the administration item dynamically
        adminNav.children.push(adminPanelItem);
      }
    }

    // Notify the subject of the navigation change
    this._onNavigationChanged.next(key);
  }

  redirectToSupport(lang: string) {
    window.open(this.getSupportLink(lang), '_blank');
  }

  getSupportLink(lang: string): string {
    return `${environment.supportURL}${lang}`;
  }

  redirectToServiceNow() {
    window.open(environment.redirectionServiceNow, '_blank');
  }
}
